import { Button, Dropdown, Tabs, TabsProps, Tooltip } from 'antd';
import Sider from 'antd/lib/layout/Sider';
import classNames from 'classnames';
import { forwardRef, useContext, useMemo } from 'react';

import Icon, { DownOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { DRAWING_TABS } from '@common/constants';
import { ShapeType } from '@common/enums';
import { DrawingTabEnum } from '@common/enums/drawing-tab.enum';
import { DrawingShape } from '@common/models';
import { useSelectedColor } from '@ee-api/colors-api/useSelectedColor';
import { useSelectedMaterial } from '@ee-api/materials-api/useSelectedMaterial';
import { DrawingShapeContext, ShapeTabContext } from '@ee-context';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { resetActiveDrawingAppliance, setNeedRedrawCanvas } from '@redux/slices/drawingShapeSlice';
import { selectSelectedDrawingTab, updateSelectedDrawingTab } from '@redux/slices/shapeSlice';

import { SelectedMaterialColor } from '../drawing-tabs/components/selected-material-color/selectedMaterialColor';
import { PredefinedShapeMenu } from '../drawing-tabs/components/shape-menu/predefinedShapeMenu';
import useFreeDrawer from '../toolbar/free-drawer/freeDrawer';
import ShapeTabContent from './shapeTabContent/shapeTabContent';
import AddShapeIcon from '/public/assets/icons/add_Shape.svg';
import DrawIcon from '/public/assets/icons/draw.svg';

import colors from '../drawing-tabs/colors.module.scss';
import './shapeTabs.scss';

const getEnabledDisabledBehavior = (drawingState: boolean): string =>
    drawingState ? colors.secondaryColor : colors.primaryColor;

// eslint-disable-next-line sonarjs/cognitive-complexity
export default forwardRef<HTMLDivElement>(function ShapeTabs(_, ref) {
    const dispatch = useAppDispatch();

    const { collapsed, updateCollapsedState } = useContext(ShapeTabContext);
    const { drawingShapes, activeDrawingShape, updateActiveDrawingShapeId, updateDrawingShapes } =
        useContext(DrawingShapeContext);

    const { selectedValue: selectedMaterial } = useSelectedMaterial();
    const { selectedValue: selectedColor } = useSelectedColor();

    const { activateWithSelectedTab } = useFreeDrawer();

    const selectedDrawingTab = useAppSelector(selectSelectedDrawingTab);

    const isFreeDrawingState = selectedDrawingTab === DrawingTabEnum.FREE_DRAWING_TAB;
    const isAddPredefinedShapeState = selectedDrawingTab === DrawingTabEnum.ADD_PREDEFINED_SHAPE_TAB;

    const rearrangedDrawingShapes = useMemo(() => {
        const filterShapes = (dShape: DrawingShape) =>
            dShape.id !== activeDrawingShape?.id && dShape.type !== ShapeType.FAUX_SHAPE;
        const tempDrawingShapes = drawingShapes.filter(filterShapes);
        return activeDrawingShape ? [activeDrawingShape, ...tempDrawingShapes] : [...tempDrawingShapes];
    }, [drawingShapes, activeDrawingShape]);

    const showShapesDropDown = useMemo(
        () =>
            !collapsed &&
            (rearrangedDrawingShapes.length > 1 || (!activeDrawingShape && rearrangedDrawingShapes.length)),
        [activeDrawingShape, collapsed, rearrangedDrawingShapes.length]
    );

    const onTabBtnClick = (activeKey: string) => {
        switch (activeKey) {
            // Free Drawing Mode
            case DrawingTabEnum.FREE_DRAWING_TAB:
                activateWithSelectedTab(DrawingTabEnum.FREE_DRAWING_TAB);
                break;

            // Add Predefined Shape Mode
            case DrawingTabEnum.ADD_PREDEFINED_SHAPE_TAB:
                if (isFreeDrawingState) {
                    activateWithSelectedTab(DrawingTabEnum.ADD_PREDEFINED_SHAPE_TAB);
                }
                dispatch(updateSelectedDrawingTab(DrawingTabEnum.ADD_PREDEFINED_SHAPE_TAB));
                break;

            // Selected Shape Tab Mode
            case DrawingTabEnum.SELECTED_SHAPE_TAB: {
                dispatch(updateSelectedDrawingTab(DrawingTabEnum.SELECTED_SHAPE_TAB));

                if (isFreeDrawingState) {
                    activateWithSelectedTab(DrawingTabEnum.SELECTED_SHAPE_TAB);
                }
                updateActiveDrawingShapeId(rearrangedDrawingShapes[0].id);

                dispatch(setNeedRedrawCanvas(true));
                break;
            }
        }
        dispatch(setNeedRedrawCanvas(false));
    };

    const onCollapse = (collapse: boolean) => {
        updateCollapsedState(collapse);
    };

    /**
     * Memoizing this variable introduces an error
     * @see https://inscyth-inc.atlassian.net/browse/EZE-1226
     */
    const dropDownItems = rearrangedDrawingShapes.map((dShape, index) => ({
        key: index + 1,
        label:
            activeDrawingShape && index === 0 ? null : (
                <div className="dropdown-item" key={index + 1}>
                    {dShape?.label}
                </div>
            ),
    }));

    const TabsItems: TabsProps['items'] = [
        {
            label: (
                <Tooltip
                    title={DRAWING_TABS.PREDEFINED_SHAPE}
                    color={getEnabledDisabledBehavior(isAddPredefinedShapeState)}
                >
                    <Icon
                        style={{ paddingLeft: '10px' }}
                        className="predefined-shape-icon"
                        component={() => (
                            <AddShapeIcon
                                fill={getEnabledDisabledBehavior(isAddPredefinedShapeState)}
                                stroke={getEnabledDisabledBehavior(isAddPredefinedShapeState)}
                            />
                        )}
                    />
                </Tooltip>
            ),
            key: DrawingTabEnum.ADD_PREDEFINED_SHAPE_TAB,
            className: 'predefined-shape-tab',
            children: <PredefinedShapeMenu shapeContainer={ref} disable={!selectedColor} />,
        },
        {
            label: (
                <Tooltip title={DRAWING_TABS.DRAW_LAYOUT} color={getEnabledDisabledBehavior(isFreeDrawingState)}>
                    <Icon
                        className="draw-icon"
                        style={{ paddingLeft: '10px' }}
                        component={() => (
                            <DrawIcon
                                fill={getEnabledDisabledBehavior(isFreeDrawingState)}
                                stroke={getEnabledDisabledBehavior(isFreeDrawingState)}
                            />
                        )}
                    />
                </Tooltip>
            ),
            key: DrawingTabEnum.FREE_DRAWING_TAB,
            className: 'draw-layout-tab',
            children: selectedColor ? (
                <SelectedMaterialColor selectedColor={selectedColor} selectedMaterial={selectedMaterial} />
            ) : null,
        },
        ...(activeDrawingShape && !!selectedColor
            ? [
                  {
                      label: (
                          <Tooltip title={rearrangedDrawingShapes[0]?.label} color={colors.primaryColor}>
                              <div
                                  style={{
                                      paddingLeft: '10px',
                                      color: colors.primaryColor,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      maxWidth: '120px',
                                  }}
                              >
                                  {rearrangedDrawingShapes[0]?.label}
                              </div>
                          </Tooltip>
                      ),
                      key: DrawingTabEnum.SELECTED_SHAPE_TAB,
                      className: 'shape-tab',
                      forceRender: true,
                      children: <ShapeTabContent shape={activeDrawingShape} />,
                  },
              ]
            : []),
        ...(showShapesDropDown
            ? [
                  {
                      label: (
                          <div className="shapes-dropdown-wrapper">
                              {selectedColor ? (
                                  <Dropdown
                                      menu={{
                                          onClick: (e) => onDropDownClick(e.key),
                                          items: dropDownItems,
                                      }}
                                      className="shapes-dropdown"
                                      placement="bottomRight"
                                      trigger={['click']}
                                  >
                                      <Button className="shapes-dropdown-button">
                                          <DownOutlined style={{ fontSize: 16 }} />
                                      </Button>
                                  </Dropdown>
                              ) : null}
                          </div>
                      ),
                      key: DrawingTabEnum.SHAPE_DROP_DOWN_BUTTON,
                  },
              ]
            : []),
    ];

    const onDropDownClick = (activeKey: string) => {
        dispatch(updateSelectedDrawingTab(DrawingTabEnum.SELECTED_SHAPE_TAB));

        if (isFreeDrawingState) {
            activateWithSelectedTab(DrawingTabEnum.SELECTED_SHAPE_TAB);
        }

        const selectedShape = rearrangedDrawingShapes[parseInt(activeKey) - 1];
        dispatch(resetActiveDrawingAppliance());
        updateActiveDrawingShapeId(selectedShape.id);
        dispatch(setNeedRedrawCanvas(true));
        /**
         * updateDrawingShapes call is needed to trigger fabricCanvas re-render
         * * @see https://inscyth-inc.atlassian.net/browse/EZE-1226
         */
        updateDrawingShapes([...drawingShapes]);
    };

    const commonDrawingShapeTabs: TabsProps = {
        activeKey: selectedDrawingTab ?? '', // NOTE: EZE-1136 ant design bug, undefined selects an item (last one?)
        onChange: onTabBtnClick,
        items: TabsItems.map((tabItem) => ({ ...tabItem, disabled: !selectedColor })),
        className: classNames('drawing-tabs', {
            'drawing-tabs__with-dropdown': showShapesDropDown,
        }),
    };

    return (
        <div className="shape-tabs">
            {rearrangedDrawingShapes.length ? (
                <Sider
                    className="drawing-tab-area"
                    onCollapse={onCollapse}
                    theme="light"
                    width={310} // needs to be the same as the css width
                    breakpoint="sm"
                    collapsedWidth={0}
                    collapsible
                    trigger={
                        <div className="shape-tab-collapse">{collapsed ? <LeftOutlined /> : <RightOutlined />}</div>
                    }
                    reverseArrow
                    ref={ref}
                >
                    <Tabs {...commonDrawingShapeTabs} />
                </Sider>
            ) : (
                <div className="drawing-tab-area">
                    <Tabs {...commonDrawingShapeTabs} />
                </div>
            )}
        </div>
    );
});
